import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  Grid,
  MenuItem,
  Modal,
  Paper,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { LabeledInput } from 'components/Molucules';
import { CitySelect } from 'components';
import { useEffect, useState, useCallback, useMemo } from 'react';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import PersonIcon from '@mui/icons-material/Person';
import PersonPinCircleIcon from '@mui/icons-material/PersonPinCircle';
import SportsScoreIcon from '@mui/icons-material/SportsScore';
import LocalTaxiIcon from '@mui/icons-material/LocalTaxi';
import { CustomAutoComplete } from 'components/Molucules';
import { Roles } from 'models';
import GoogleMapsAutoComplete from 'components/GoogleMapsAutoComplete';
import { useAddBooking } from './hooks';
import { LabeledText } from 'components/Molucules';
import { GoogleMapApiConfig } from 'config/GoogleMapApiConfig';
import GoogleMapReact from 'google-map-react';
import { CustomMapMark } from 'views/BookingHistory';
import Swal from 'sweetalert2';
import { ExpandMore } from '@mui/icons-material';
import { APIProvider, Map, Marker, useMarkerRef } from '@vis.gl/react-google-maps';

const AddBookingPage = () => {
  const {
    confirmBooking,
    estimateRouteDetails,
    createNewBasicUser,
    t,
    loadCustomers,
    loadDrivers,
    updateSelectedCustomer,
    updateCustomerName,
    updatePickupAddress,
    updateDropAddress,
    updateCustomerPhone,
    setEstimateModalStatus,
    loadCurrentUser,
    updateCarDetails,
    updateBasicUser,
    estimateModalStatus,
    loading,
    customers,
    rideData,
    auth,
    usersData,
    estimateData,
    settings,
    name,
    cartypes,
    city,
    setCity,
  } = useAddBooking();

  const DEFAULT_CENTER = { lat: 7.89391, lng: -72.50782 };
  const ADDITIONAL_OPTIONS = [
    'Baúl grande',
    'Aire acondicionado',
    'Mascotas',
    'Silla de ruedas',
    'Parrilla',
    'Transferencia',
  ];

  const [markerRef, marker] = useMarkerRef();
  const [mapProps, setMapProps] = useState({
    center: DEFAULT_CENTER,
    zoom: 14,
    pendingSelection: null,
  });

  const [aditionalOptions, setAditionalOptions] = useState(
    ADDITIONAL_OPTIONS.map(name => ({ name, value: false }))
  );

  const isValidCoordinate = useCallback((coord) => {
    return typeof coord === 'number' && !isNaN(coord) && isFinite(coord);
  }, []);

  const handleAditionalOptions = useCallback((event, option) => {
    setAditionalOptions(prevOptions =>
      prevOptions.map(item =>
        item.name === option ? { ...item, value: event.target.checked } : item,
      ),
    );
  }, []);

  const getAdditionalOptions = useCallback(() => {
    const selectedOptions = aditionalOptions.filter(option => option.value === true);
    return selectedOptions.map(option => option.name).join(' - ') + (selectedOptions.length > 0 ? ' - ' : '');
  }, [aditionalOptions]);

  const calculateMapCenter = useCallback(() => {
    if (!rideData.pickupAddress && !rideData.dropAddress) {
      return DEFAULT_CENTER;
    }

    if (rideData.pickupAddress && !rideData.dropAddress) {
      const lat = rideData.pickupAddress.coords?.lat;
      const lng = rideData.pickupAddress.coords?.lng;
      
      if (isValidCoordinate(lat) && isValidCoordinate(lng)) {
        return { lat, lng };
      }
      return DEFAULT_CENTER;
    }
    
    if (!rideData.pickupAddress && rideData.dropAddress) {
      const lat = rideData.dropAddress.coords?.lat;
      const lng = rideData.dropAddress.coords?.lng;
      
      if (isValidCoordinate(lat) && isValidCoordinate(lng)) {
        return { lat, lng };
      }
      return DEFAULT_CENTER;
    }
    
    const pickupLat = rideData.pickupAddress.coords?.lat;
    const pickupLng = rideData.pickupAddress.coords?.lng;
    const dropLat = rideData.dropAddress.coords?.lat;
    const dropLng = rideData.dropAddress.coords?.lng;
    
    if (isValidCoordinate(pickupLat) && isValidCoordinate(pickupLng) && 
        isValidCoordinate(dropLat) && isValidCoordinate(dropLng)) {
      const lat = (pickupLat + dropLat) / 2;
      const lng = (pickupLng + dropLng) / 2;
      return { lat, lng };
    }
    
    return DEFAULT_CENTER;
  }, [rideData.pickupAddress, rideData.dropAddress, isValidCoordinate]);

  const handleMapClick = useCallback((event) => {
    if (!isValidCoordinate(event.lat) || !isValidCoordinate(event.lng)) return;
    
    setMapProps(prevProps => ({
      ...prevProps,
      center: { lat: event.lat, lng: event.lng },
      pendingSelection: { lat: event.lat, lng: event.lng },
    }));
  }, [isValidCoordinate]);

  const handleSetAsOrigin = useCallback((coords) => {
    updatePickupAddress(coords);
    setMapProps(prevProps => ({
      ...prevProps,
      pendingSelection: null,
    }));
  }, [updatePickupAddress]);

  const handleSetAsDestination = useCallback((coords) => {
    updateDropAddress(coords);
    setMapProps(prevProps => ({
      ...prevProps,
      pendingSelection: null,
    }));
  }, [updateDropAddress]);

  const handleCancelSelection = useCallback(() => {
    setMapProps(prevProps => ({
      ...prevProps,
      pendingSelection: null,
    }));
  }, []);

  const handleRegisterUser = async () => {
    try {
      if (!name.trim() && !rideData.customer.firstName) {
        Swal.fire({
          icon: 'warning',
          title: 'Datos incompletos',
          text: 'Por favor ingrese el nombre del pasajero',
        });
        return;
      }

      if (!rideData.customer.mobile) {
        Swal.fire({
          icon: 'warning',
          title: 'Datos incompletos',
          text: 'Por favor ingrese el teléfono del pasajero',
        });
        return;
      }

      const result = await Swal.fire({
        title: 'Registrando usuario',
        text: '¿Está seguro de registrar este usuario?',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Sí, registrar',
        cancelButtonText: 'Cancelar'
      });

      if (result.isConfirmed) {
        await createNewBasicUser();
        Swal.fire({
          icon: 'success',
          title: 'Éxito',
          text: 'Usuario registrado exitosamente',
        });
      }
    } catch (error) {
      console.error('Error registrando usuario:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'Error al registrar el usuario',
      });
    }
  };

  const handleUpdateUser = async () => {
    try {
      const result = await Swal.fire({
        title: 'Actualizando usuario',
        text: '¿Está seguro de actualizar este usuario?',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Sí, actualizar',
        cancelButtonText: 'Cancelar'
      });

      if (result.isConfirmed) {
        await updateBasicUser(rideData.customer);
        Swal.fire({
          icon: 'success',
          title: 'Éxito',
          text: 'Usuario actualizado exitosamente',
        });
      }
    } catch (error) {
      console.error('Error actualizando usuario:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'Error al actualizar el usuario',
      });
    }
  };

  useEffect(() => {
    loadCustomers();
    loadDrivers();
    loadCurrentUser();
  }, [usersData]);

  useEffect(() => {
    const newCenter = calculateMapCenter();
    setMapProps(prevProps => ({
      ...prevProps,
      center: newCenter,
      zoom: (rideData.pickupAddress && rideData.dropAddress) ? 13 : 14,
    }));
  }, [rideData.pickupAddress, rideData.dropAddress]);

  // Categorías disponibles para la ciudad del operador, ordenadas por `pos`
  // (mismo orden que ve el pasajero en la app). Si ninguna fila trae `city`
  // que coincida, se muestran todas.
  const sortedCarTypes = useMemo(() => {
    const list = cartypes || [];
    const city = auth?.profile?.city;
    const byCity = city ? list.filter(ct => ct.city === city) : [];
    const base = byCity.length > 0 ? byCity : list;
    return [...base].sort((a, b) => (Number(a.pos) || 0) - (Number(b.pos) || 0));
  }, [cartypes, auth]);

  // Fija la categoría de menor `pos` sólo si aún no hay una selección válida.
  useEffect(() => {
    if (sortedCarTypes.length === 0) return;
    const current = rideData.carDetails;
    const stillValid =
      current && sortedCarTypes.some(ct => ct.id === current.id);
    if (!stillValid) {
      updateCarDetails(sortedCarTypes[0]);
    }
  }, [sortedCarTypes]);

  if (loading) {
    return <div>CARGANDO...</div>;
  }

  return (
    <Box sx={{ height: '90vh', display: 'flex', position: 'relative', padding: '0 !important' }}>
      <Paper 
        sx={{ 
          width: 380,
          display: 'flex', 
          flexDirection: 'column',
          zIndex: 1000,
          position: 'relative',
          borderRadius: 0,
          borderRight: '1px solid',
          borderColor: 'divider',
          padding: 0
        }}
      >
        <Box sx={{ 
          p: 2, 
          borderBottom: '1px solid', 
          borderColor: 'divider',
          backgroundColor: 'background.paper'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
            Reservar Viaje
          </Typography>
        </Box>

        <Box sx={{ 
          flexGrow: 1, 
          overflowY: 'auto',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2
        }}>
          {customers && auth.profile.usertype !== Roles.CUSTOMER && (
            <Box>
              <CustomAutoComplete
                label={'Teléfono del pasajero'}
                placeholder={'Teléfono'}
                value={rideData.customer ? rideData.customer.mobile : ''}
                updateValue={user => {
                  console.log(user);
                  updateSelectedCustomer(user);
                }}
                updateInputValue={text => {
                  updateCustomerPhone(text);
                }}
                icon={<PhoneAndroidIcon />}
                options={customers}
              />
            </Box>
          )}

          <Box>
            {auth.profile.usertype !== Roles.CUSTOMER && rideData.customer?.firstName ? (
              <LabeledText
                label={'Nombre del pasajero'}
                icon={<PersonIcon />}
                text={`${rideData.customer.firstName} ${rideData.customer.lastName}`}
              />
            ) : (
              <LabeledInput
                value={name}
                label={'Nombre del pasajero'}
                placeholder={'Nombre'}
                updateText={updateCustomerName}
                icon={<PersonIcon />}
              />
            )}
          </Box>

          {auth.profile.usertype !== Roles.CUSTOMER && (
            <Box>
              <CitySelect
                label="Ciudad del pasajero"
                size="small"
                value={city || ''}
                onChange={value => setCity(value)}
              />
            </Box>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <GoogleMapsAutoComplete
              key={`pickup-${rideData.customer?.uid || 'anonymous'}`}
              placeholder={'Dirección de recogida'}
              value={rideData.pickupAddress}
              onChange={address => {
                updatePickupAddress(address);
              }}
              icon={<PersonPinCircleIcon />}
              externalValue={rideData.pickupAddress?.addressName || rideData.pickupAddress?.add || ''}
            />

            <GoogleMapsAutoComplete
              key={`drop-${rideData.customer?.uid || 'anonymous'}`}
              placeholder={'Dirección de destino'}
              value={rideData.dropAddress}
              onChange={address => {
                updateDropAddress(address);
              }}
              icon={<SportsScoreIcon />}
              externalValue={rideData.dropAddress?.add}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              fullWidth
              size="small"
              sx={{
                backgroundColor: 'primary.main',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
                textTransform: 'none',
                fontWeight: 600
              }}
              onClick={handleRegisterUser}
            >
              Registrar
            </Button>
            
            {rideData.customer?.uid && (
              <Button
                fullWidth
                variant="outlined"
                size="small"
                sx={{
                  textTransform: 'none',
                  fontWeight: 600
                }}
                onClick={handleUpdateUser}
              >
                Actualizar
              </Button>
            )}
          </Box>

          {sortedCarTypes.length > 0 && (
            <Box>
              <TextField
                select
                fullWidth
                size="small"
                label="Categoría de servicio"
                value={rideData.carDetails?.id || ''}
                onChange={e => {
                  const picked = sortedCarTypes.find(
                    ct => ct.id === e.target.value,
                  );
                  if (picked) updateCarDetails(picked);
                }}
              >
                {sortedCarTypes.map(ct => (
                  <MenuItem key={ct.id} value={ct.id}>
                    {ct.name}
                    {ct.city ? ` — ${ct.city}` : ''}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          )}

          <Accordion sx={{ boxShadow: 'none', border: '1px solid', borderColor: 'divider' }}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Opciones adicionales
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <FormGroup>
                <Grid container spacing={0}>
                  {ADDITIONAL_OPTIONS.map((option, index) => (
                    <Grid item xs={12} key={`grid-${option}-${index}`}>
                      <FormControlLabel
                        control={
                          <Switch 
                            size="small"
                            onChange={e => handleAditionalOptions(e, option)} 
                          />
                        }
                        label={<Typography variant="body2">{option}</Typography>}
                        sx={{ my: 0.5 }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </FormGroup>
            </AccordionDetails>
          </Accordion>
        </Box>

        <Box sx={{ 
          p: 2, 
          borderTop: '1px solid', 
          borderColor: 'divider',
          backgroundColor: 'background.paper'
        }}>
          <Button
            fullWidth
            variant="contained"
            size="large"
            sx={{
              backgroundColor: '#4CAF50',
              '&:hover': {
                backgroundColor: '#45a049',
              },
              py: 1.5,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '1rem'
            }}
            startIcon={<LocalTaxiIcon />}
            onClick={estimateRouteDetails}
          >
            Reservar Viaje
          </Button>
        </Box>
      </Paper>

      <Box sx={{ 
        flexGrow: 1, 
        position: 'relative',
        height: '100%'
      }}>
        <APIProvider apiKey={GoogleMapApiConfig}>
          <Map
            style={{width: '100%', height: '100%'}}
            defaultZoom={mapProps.zoom}
            gestureHandling='greedy'
            disableDefaultUI={false}
            onClick={(e) => {
              console.log('Map clicked at: ', e.detail.latLng);
              handleMapClick({...e.detail.latLng});
            }}
            center={mapProps.center}
            onDragstart={(e) => {
              setMapProps(prevProps => ({
                ...prevProps,
                center: null
              }));
            }}
          >
            {mapProps.pendingSelection &&
            isValidCoordinate(mapProps.pendingSelection.lat) &&
            isValidCoordinate(mapProps.pendingSelection.lng) &&
            CustomMapMark.createSelectPlaceMarket({
              coords: mapProps.pendingSelection,
              title: 'P',
              setOrigin: handleSetAsOrigin,
              setDestination: handleSetAsDestination,
              setNewCoords: handleCancelSelection,
            })}

            {rideData.pickupAddress?.coords &&
            isValidCoordinate(rideData.pickupAddress.coords.lat) &&
            isValidCoordinate(rideData.pickupAddress.coords.lng) &&
            <Marker
              position={{
                lat: rideData.pickupAddress.coords.lat,
                lng: rideData.pickupAddress.coords.lng
              }}
              icon={{
                url: "https://maps.google.com/mapfiles/ms/icons/green-dot.png",
                scaledSize: { width: 32, height: 32 }
              }}
            />
          }

          {rideData.dropAddress?.coords &&
            isValidCoordinate(rideData.dropAddress.coords.lat) &&
            isValidCoordinate(rideData.dropAddress.coords.lng) &&
            <Marker
              position={{
                lat: rideData.dropAddress.coords.lat,
                lng: rideData.dropAddress.coords.lng
              }}
              icon="https://maps.google.com/mapfiles/ms/icons/red-dot.png"
            />}
          </Map>
        </APIProvider>
      </Box>

      <Modal
        disablePortal
        disableEnforceFocus
        disableAutoFocus
        open={estimateModalStatus}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Paper
          elevation={8}
          sx={{
            width: 400,
            p: 4,
            borderRadius: 3,
            textAlign: 'center'
          }}
        >
          <Typography
            variant="h4"
            color="primary"
            sx={{ 
              mb: 3,
              fontWeight: 600
            }}
          >
            {t('total')} - {estimateData.estimate?.estimateFare || ''}{settings?.symbol || ''}
          </Typography>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                onClick={() => {
                  setEstimateModalStatus(false);
                }}
                variant="outlined"
                color="error"
                size="large"
                sx={{ 
                  minWidth: 120,
                  textTransform: 'none',
                  fontWeight: 600
                }}
              >
                {t('cancel')}
              </Button>
              <Button
                onClick={e => confirmBooking(e, getAdditionalOptions())}
                variant="contained"
                color="primary"
                size="large"
                sx={{ 
                  minWidth: 120,
                  textTransform: 'none',
                  fontWeight: 600
                }}
              >
                {t('book_now')}
              </Button>
            </Box>
          )}
        </Paper>
      </Modal>
    </Box>
  );
};
export default AddBookingPage;
